import type { ObItemWithSubmission, ObBranchingRule } from "./onboarding-api";

/**
 * Evaluate visibility of onboarding items based on branching rules.
 * Similar to the visit form branching but uses parent/child item IDs.
 */
export function evaluateOnboardingVisibility(
    items: ObItemWithSubmission[],
    answers: Record<string, string>
): Set<string> {
    const visible = new Set<string>();

    // Build a map of parent → child rules
    const childRules = new Map<string, ObBranchingRule[]>();
    for (const item of items) {
        for (const rule of item.branchingRules) {
            const existing = childRules.get(rule.childItemId) ?? [];
            existing.push(rule);
            childRules.set(rule.childItemId, existing);
        }
    }

    const byId = new Map(items.map((i) => [i.id, i]));

    // Visibility is TRANSITIVE: a child is only visible if its own rule is
    // satisfied AND the parent that rule points at is itself visible.
    //
    // Without the second half, a stale answer keeps a grandchild on screen after
    // its parent disappears. Real case: "How would you like to verify your work
    // history?" -> "What is your relationship with them?" -> "Please describe
    // your relationship". Pick Reference, then Other, then switch back to Proof
    // Of Work: the relationship question hides, but its answer is still "Other",
    // so the describe field kept passing its own rule and stayed visible on the
    // proof-of-work path where it makes no sense.
    const memo = new Map<string, boolean>();
    const resolving = new Set<string>();

    function isVisible(id: string): boolean {
        const cached = memo.get(id);
        if (cached !== undefined) {
            return cached;
        }
        // A cycle in the rules would otherwise recurse forever; treat it as
        // hidden rather than trusting a self-referential chain.
        if (resolving.has(id)) {
            return false;
        }
        resolving.add(id);

        let result: boolean;
        const item = byId.get(id);
        if (!item || item.hiddenOnMobile) {
            result = false;
        } else {
            const rules = childRules.get(id);
            if (!rules || rules.length === 0) {
                result = true;
            } else {
                result = rules.some(
                    (rule) =>
                        (answers[rule.parentItemId] ?? "") === rule.parentValue &&
                        isVisible(rule.parentItemId)
                );
            }
        }

        resolving.delete(id);
        memo.set(id, result);
        return result;
    }

    for (const item of items) {
        if (isVisible(item.id)) {
            visible.add(item.id);
        }
    }

    return visible;
}

/**
 * Does this item count toward what the nurse still has to do?
 *
 * Counting keys on SUBMISSION STATUS, never on the config `mandatory` flag.
 * That is the rule `onboarding-progress.ts` states on the server, in as many
 * words, and for a concrete reason: follow-up chains legitimately flip a
 * NON-mandatory item from `not_required` to `required`, and once raised it is
 * outstanding work like any other. A positive TB test raises the provider
 * clearance note; a two-step PPD raises the second reading; a licence in a
 * different name raises the name-change document.
 *
 * Filtering on `mandatory` here is what Shantell Hagins saw on 14 Sep 2026:
 * "Medical & Health — Complete — 100%" on the hub, while opening it showed
 * "Tests, Labs & Vaccines 6/7" with her second-step PPD still required. The
 * same filter told `computeAllSubmitted` she was finished, so the hub also
 * showed "Application under review — You've completed your onboarding" above a
 * bar reading "1 items remaining, 98%". Three statements on one screen, two of
 * them wrong.
 *
 * `can_after` is excluded, matching the server: such an item never holds up
 * activation even once something flips it to `required`. It exists to OFFER her
 * a way out of a problem rather than to ask something of her — the legal
 * name-change upload is the case — so counting it would leave her one item
 * short of 100% with no way to clear it.
 */
function countsTowardProgress(item: ObItemWithSubmission): boolean {
    return (
        isItemInThisOnboarding(item) &&
        item.activationGate !== "can_after" &&
        item.submission?.status !== "not_required"
    );
}

/**
 * Is this config item actually part of THIS nurse's onboarding?
 *
 * No submission row means the item was never seeded for her: a legacy-only
 * section a native applicant never got ("Migrated Compliance"), or — far more
 * often — a config item added to the flow after she started. The server already
 * skips these (`onboarding-progress.ts`: "No submission row for this RN"), and
 * counting them here is what made the two disagree.
 *
 * That disagreement is not cosmetic. A mandatory config item with no row can
 * never be completed by the nurse (there is nothing to open, nothing to
 * upload), so it pinned its section below 100% forever — and because the final
 * group (Direct Deposit / W9 / RN Contract) is `unlockAfterRequired`, it stayed
 * locked for good. That is exactly how the two migrated "Rubella"/"Rubeola"
 * items locked every imported nurse out of the end of her application while the
 * admin side happily showed her at 100%.
 *
 * `plain_text` is skipped for the same reason the server skips it: it is
 * something she READS, has no input, and can never reach `approved`.
 */
function isItemInThisOnboarding(item: ObItemWithSubmission): boolean {
    return item.submission != null && item.itemType !== "plain_text";
}

/**
 * Visibility-aware progress for ONE subsection, counted by submission status.
 *
 * The single place this is computed. The section screen used to carry its own
 * inline copy of the old mandatory-only count, so fixing the hub on 15 Sep 2026
 * left it behind: the hub read "Medical & Health 96%" while tapping in showed
 * "Tests, Labs & Vaccines" as complete. A second copy is how a fix gets half
 * applied, so there is now one.
 */
export function computeSubsectionProgress(subsection: { items: ObItemWithSubmission[] }): {
    approved: number;
    total: number;
} {
    const answers: Record<string, string> = {};
    for (const item of subsection.items) {
        if (item.submission?.answerValue) {
            answers[item.id] = item.submission.answerValue;
        }
    }
    const visibleIds = evaluateOnboardingVisibility(subsection.items, answers);
    const counted = subsection.items.filter((i) => visibleIds.has(i.id) && countsTowardProgress(i));
    return {
        approved: counted.filter((i) => i.submission?.status === "approved").length,
        total: counted.length
    };
}

/**
 * Visibility-aware progress for a section: the sum of its subsections.
 *
 * Shared by the hub and the section screen so their "N items left / X%" never
 * disagree, and aligned with `onboarding-progress.ts` on the server so the
 * nurse's screen never disagrees with the reviewer's either.
 */
export function computeSectionProgress(section: {
    subsections: Array<{ items: ObItemWithSubmission[] }>;
}): { approved: number; total: number } {
    let approved = 0;
    let total = 0;
    for (const sub of section.subsections) {
        const p = computeSubsectionProgress(sub);
        approved += p.approved;
        total += p.total;
    }
    return { approved, total };
}

/**
 * Has the nurse handed in every visible counting item of every must_before
 * section? "Handed in" = submitted, ai_verifying, approved, or not_required —
 * from her point of view the application is done and under review, even while
 * verification is still churning. Drives the one-time submitted celebration
 * and the persistent under-review card. Mirrors the server's
 * `progress.allSubmitted` (onboarding-progress.ts) but is computed from the
 * already-loaded config so the hub doesn't need an extra fetch.
 */
export function computeAllSubmitted(
    config: Array<{
        activationGate: "must_before" | "can_after";
        subsections: Array<{ items: ObItemWithSubmission[] }>;
    }>
): boolean {
    const DONE = new Set(["submitted", "ai_verifying", "approved", "not_required"]);
    for (const section of config) {
        if (section.activationGate !== "must_before") {
            continue;
        }
        for (const sub of section.subsections) {
            const answers: Record<string, string> = {};
            for (const item of sub.items) {
                if (item.submission?.answerValue) {
                    answers[item.id] = item.submission.answerValue;
                }
            }
            const visibleIds = evaluateOnboardingVisibility(sub.items, answers);
            for (const item of sub.items) {
                if (!visibleIds.has(item.id) || !countsTowardProgress(item)) {
                    continue;
                }
                if (!DONE.has(item.submission?.status ?? "required")) {
                    return false;
                }
            }
        }
    }
    return true;
}

/**
 * Is this section done ENOUGH to unlock the final paperwork group?
 *
 * Deliberately a weaker test than `computeSectionProgress`, and the difference
 * is the point. Progress is about telling her the truth; this is about when to
 * open a door, and the two want different answers.
 *
 * A chain-raised item is real outstanding work, so it belongs in her progress.
 * But it is often work she cannot do today: a second-step PPD has to be placed
 * 1 to 3 weeks after the first reading, and a provider clearance note waits on
 * an appointment. Gating on it would lock Shantell out of her bank details and
 * her W-9 for a fortnight over a skin test - paperwork that has nothing to do
 * with TB and that she could otherwise finish while she waits.
 *
 * The gate exists to stop her filling in bank details before she has shown she
 * is employable at all, and every mandatory item still has to be approved for
 * that. Nothing here lets her be ACTIVATED early: the server decides that, and
 * it counts by status (`onboarding-progress.ts`).
 */
function countsTowardUnlock(item: ObItemWithSubmission): boolean {
    return item.mandatory && countsTowardProgress(item);
}

export function isSectionComplete(section: {
    subsections: Array<{ items: ObItemWithSubmission[] }>;
}): boolean {
    for (const sub of section.subsections) {
        const answers: Record<string, string> = {};
        for (const item of sub.items) {
            if (item.submission?.answerValue) {
                answers[item.id] = item.submission.answerValue;
            }
        }
        const visibleIds = evaluateOnboardingVisibility(sub.items, answers);
        for (const item of sub.items) {
            if (!visibleIds.has(item.id) || !countsTowardUnlock(item)) {
                continue;
            }
            if (item.submission?.status !== "approved") {
                return false;
            }
        }
    }
    return true;
}

/**
 * The final pre-activation group (Direct Deposit, W9, RN Contract) is flagged
 * `unlockAfterRequired` and stays LOCKED until every OTHER required section
 * (Identity, Medical, Insurance, Orientation) is complete. This computes
 * whether all those prior required sections are done.
 */
export function areRequiredSectionsComplete(
    sections: Array<{
        unlockAfterRequired?: boolean;
        subsections: Array<{ items: ObItemWithSubmission[] }>;
    }>
): boolean {
    return sections.filter((s) => !s.unlockAfterRequired).every((s) => isSectionComplete(s));
}

/** True when a section is gated (unlockAfterRequired) and not yet unlocked. */
export function isSectionLocked(
    section: { unlockAfterRequired?: boolean },
    allSections: Array<{
        unlockAfterRequired?: boolean;
        subsections: Array<{ items: ObItemWithSubmission[] }>;
    }>
): boolean {
    return !!section.unlockAfterRequired && !areRequiredSectionsComplete(allSections);
}
